<script setup lang="ts">
// NPM
import { onBeforeMount, reactive, ref, computed } from 'vue';
import { cloneDeep } from 'lodash';
import { useI18n } from 'vue-i18n';

// Components
import { BaseLoader, FieldSelect, BaseButton } from '@magma-app/magma-lapilli';

// Stores
import { useUserStore } from '@/stores/user';
import { useCommonStore } from '@/stores/common';
import { useAsyncAction } from '@/composables/useAsyncAction';
const userStore = useUserStore();
const commonStore = useCommonStore();
const { isRunning: isSaving, run } = useAsyncAction();

// Plugins
const { t } = useI18n({ useScope: 'global' });

// Reactive variables
const initialized = ref(false);

const formData = reactive({
  criteria: {} as { [key: string]: string | string[] },
});
const formDataSaved = reactive(cloneDeep(formData));

// Lifecycle hooks
onBeforeMount(async () => {
  await userStore.getUserCriteria();
  await userStore.getCampaigns(); // Get signed-up campaigns

  if (userStore.userCriteria) {
    formData.criteria = userStore.userCriteria?.reduce(
      (accumulator: { [key: string]: string | string[] }, criteria) => {
        if (criteria.type === 'singleSelect') {
          accumulator[criteria.id] = criteria.userAnswerKey || '';
        } else {
          accumulator[criteria.id] = criteria.userAnswerKeys || [];
        }
        return accumulator;
      },
      {}
    ) as { [key: string]: string | string[] };

    // Clone the initial data for comparison
    formDataSaved.criteria = cloneDeep(formData.criteria);
  }

  initialized.value = true;
});

// Methods
const hasChanges = () => {
  return (
    JSON.stringify(formData.criteria) !== JSON.stringify(formDataSaved.criteria)
  );
};

const saveAllCriteria = () => run(async () => {
  if (!hasChanges()) return;

  try {
    const updatePromises: Promise<unknown>[] = [];

    // Compare current form data with saved data to find changes
    for (const [criteriaId, criteriaValue] of Object.entries(
      formData.criteria
    )) {
      const savedValue = formDataSaved.criteria[criteriaId];

      if (JSON.stringify(criteriaValue) !== JSON.stringify(savedValue)) {
        const objectKey =
          typeof criteriaValue === 'object' ? 'answerKeys' : 'answerKey';

        // Only update if value is not empty or is a valid array
        if (
          objectKey !== 'answerKeys' ||
          (Array.isArray(criteriaValue) && criteriaValue.length > 0)
        ) {
          updatePromises.push(
            userStore.updateUserCriteria({
              criteriaId: parseInt(criteriaId),
              [objectKey]: criteriaValue,
            })
          );
        }
      }
    }

    if (updatePromises.length > 0) {
      await Promise.all(updatePromises);

      // Update saved data
      formDataSaved.criteria = cloneDeep(formData.criteria);

      // Show success toast notification
      commonStore.setAlert({
        open: true,
        type: 'success',
        title: t('generic.informationUpdated'),
      });
    }
  } catch (error) {
    console.log(error);
  }
});

const getCriteriaOptions = (id: number) => {
  let campaignsCriteria = undefined;

  if (userStore.userCriteria) {
    campaignsCriteria = userStore.userCriteria.find(
      (criteria) => criteria.id === id
    );
  }

  if (campaignsCriteria?.options) {
    return campaignsCriteria.options.map(
      (criteriaOption: { key: string; value: string }) => ({
        label: criteriaOption.value,
        value: criteriaOption.key,
      })
    );
  } else {
    return [];
  }
};

// Computed properties to group criteria by campaigns
const campaignsWithCriteria = computed(() => {
  if (!userStore.campaigns || !userStore.userCriteria) return [];

  return userStore.campaigns
    .map((campaign) => {
      const campaignCriteria = userStore.userCriteria.filter(
        (userCriterion) => {
          return campaign.id === userCriterion.campaignId;
        }
      );

      return {
        id: campaign.id,
        name: campaign.title, // Use title instead of name
        criteria: campaignCriteria,
      };
    })
    .filter((campaign) => campaign.criteria.length > 0);
});
</script>

<template>
  <template v-if="initialized">
    <div class="flex flex-col gap-8">
      <div class="flex flex-row gap-8 items-start justify-between">
        <div class="flex flex-col gap-2 w-2/3">
          <h2 class="text-2xl font-bold leading-7 text-foreground-emphasis">
            {{ $t('settingsMatchingDetailsView.setMatching') }}
          </h2>
          <p class="text-sm text-foreground-emphasis">
            {{ $t('settingsMatchingDetailsView.matchingDescription') }}
          </p>
        </div>
        <!-- IF THERE'S NO MODIFICATIONS: DISABLE THE BUTTON -->
        <BaseButton
          :text="$t('generic.save')"
          size="md"
          class="w-1/3"
          :disabled="isSaving || !hasChanges()"
          @click="saveAllCriteria"
        />
      </div>

      <!-- 
        GROUP CRITERIA BY CAMPAIGN
        FOR EACH CAMPAIGN: 1 DIV GROUP BELOW
      -->
      <div
        v-for="campaign in campaignsWithCriteria"
        :key="campaign.id"
        class="bg-background-subtle rounded-lg"
      >
        <p class="px-6 py-4 text-mgm-txt-sm text-foreground-emphasis">
          {{ campaign.name }}
        </p>
        <div class="p-6 border border-border-subtle rounded-lg bg-white">
          <FieldSelect
            v-for="campaignsCriteriaItem in campaign.criteria"
            :id="campaignsCriteriaItem.id"
            :key="campaignsCriteriaItem.id"
            v-model="formData.criteria[campaignsCriteriaItem.id]"
            class="mb-5"
            :label="campaignsCriteriaItem.label"
            :options="getCriteriaOptions(campaignsCriteriaItem.id)"
            :multiple="campaignsCriteriaItem.type === 'multiSelect'"
          />
        </div>
      </div>
    </div>
  </template>

  <BaseLoader v-else class="min-h-screen" />
</template>
